import React from "react";
import { motion } from "framer-motion";
import { Link, useParams, Navigate } from "react-router-dom";
import { LogIn, LayoutDashboard, QrCode, Newspaper, CalendarDays, Tag, Bell, BarChart3, ChevronLeft, Check, Lightbulb, ArrowRight, MessageCircle } from "lucide-react";
import { GUIDE_MODULES } from "../data/content";
import { Seo } from "./Seo";

const ICONS = { LogIn, LayoutDashboard, QrCode, Newspaper, CalendarDays, Tag, Bell, BarChart3 };

export const GuideDetail = () => {
  const { slug } = useParams();
  const index = GUIDE_MODULES.findIndex((m) => m.slug === slug);
  if (index === -1) return <Navigate to="/guide" replace />;

  const m = GUIDE_MODULES[index];
  const Icon = ICONS[m.icon];
  const others = GUIDE_MODULES.filter((g) => g.slug !== slug).slice(0, 3);

  const BASE = "https://mypushapp.it";
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "HowTo",
      name: m.title,
      description: m.intro,
      step: m.steps.map((s, j) => ({
        "@type": "HowToStep",
        position: j + 1,
        name: `Passo ${j + 1}`,
        text: s,
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${BASE}/` },
        { "@type": "ListItem", position: 2, name: "Guide", item: `${BASE}/guide` },
        { "@type": "ListItem", position: 3, name: m.title, item: `${BASE}/guide/${m.slug}` },
      ],
    },
  ];

  return (
    <section className="py-20 sm:py-28" data-testid="guide-detail-section">
      <Seo
        title={`${m.title} | Guida PushApp`}
        description={m.intro}
        path={`/guide/${m.slug}`}
        ogTitle={`${m.title} | Guida PushApp`}
        ogDescription={m.intro}
        jsonLd={jsonLd}
      />
      <div className="mx-auto max-w-3xl px-5 sm:px-8">
        <Link to="/guide" data-testid="guide-back-link" className="inline-flex items-center gap-1.5 font-body text-sm font-semibold text-brand hover:gap-2.5 transition-all">
          <ChevronLeft size={16} strokeWidth={2.5} /> Tutte le guide
        </Link>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mt-6">
          <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-brand text-white shadow-lg shadow-brand/25">
            <Icon size={28} strokeWidth={2.5} />
          </span>
          <h1 className="mt-5 font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-brand-deep leading-[1.1]">{m.title}</h1>
          <p className="mt-4 font-body text-lg text-ink leading-relaxed">{m.intro}</p>
        </motion.div>

        {/* Steps */}
        <div className="mt-12 rounded-3xl bg-[#F9FAFB] border border-gray-100 p-7 sm:p-9" data-testid="guide-steps">
          <h2 className="font-heading text-2xl font-bold text-brand-deep">Come si fa</h2>
          <ol className="mt-6 space-y-4">
            {m.steps.map((s, j) => (
              <li key={j} className="flex items-start gap-4" data-testid={`guide-step-${j}`}>
                <span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-brand text-white font-heading font-bold text-sm">{j + 1}</span>
                <span className="font-body text-ink text-[16px] leading-relaxed pt-0.5">{s}</span>
              </li>
            ))}
          </ol>
        </div>

        {/* Tips */}
        {m.tips?.length > 0 && (
          <div className="mt-6 rounded-3xl bg-brand-green/10 border border-brand-green/20 p-7 sm:p-9" data-testid="guide-tips">
            <div className="flex items-center gap-2 text-brand-deep">
              <Lightbulb size={20} strokeWidth={2.5} className="text-brand-green" />
              <h2 className="font-heading text-xl font-bold">Consigli</h2>
            </div>
            <ul className="mt-5 space-y-3">
              {m.tips.map((t, j) => (
                <li key={j} className="flex items-start gap-3 font-body text-ink">
                  <Check size={18} className="mt-0.5 flex-none text-brand-green" strokeWidth={3} />
                  <span className="text-[15px] leading-relaxed">{t}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Ask Asia CTA */}
        <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 rounded-3xl bg-brand-deep p-7 text-white">
          <div>
            <p className="font-heading text-lg font-bold">Hai ancora un dubbio?</p>
            <p className="font-body text-white/75 text-sm mt-1">Chiedi ad Asia, l'assistente AI: ti risponde subito.</p>
          </div>
          <button
            onClick={() => window.dispatchEvent(new Event("open-asia"))}
            data-testid="guide-ask-asia"
            className="inline-flex flex-none items-center gap-2 rounded-full bg-white px-6 py-3 font-body font-semibold text-brand transition-all duration-300 hover:-translate-y-0.5"
          >
            <MessageCircle size={18} strokeWidth={2.5} /> Chiedi ad Asia
          </button>
        </div>

        {/* Other guides */}
        <div className="mt-14">
          <h2 className="font-heading text-xl font-bold text-brand-deep">Altre guide</h2>
          <div className="mt-5 grid sm:grid-cols-3 gap-4">
            {others.map((g) => {
              const OIcon = ICONS[g.icon];
              return (
                <Link
                  key={g.slug}
                  to={`/guide/${g.slug}`}
                  data-testid={`guide-related-${g.slug}`}
                  className="group rounded-2xl bg-white border border-gray-100 p-5 shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg"
                >
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-brand-light/30 text-brand"><OIcon size={20} strokeWidth={2.5} /></span>
                  <p className="mt-3 font-heading font-semibold text-brand-deep text-[15px]">{g.title}</p>
                  <span className="mt-2 inline-flex items-center gap-1 font-body text-xs font-semibold text-brand group-hover:gap-2 transition-all">Apri <ArrowRight size={13} strokeWidth={2.5} /></span>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
